import { useEffect } from 'react';
import { roundToDecimals, getFormattedMoney } from '@/components/thanks-page/utils/helpers';
import { calculateDebtAnalysisRaw } from '@/components/thanks-page/utils/calculator';
import useThanksPageStore from '@/components/thanks-page/hooks/useThanksPageStore';
import Dialog from '@/components/dialog';
import Quiz from '@/components/quiz';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { SliderSlice } from '@/redux/slider';
import '@/components/thanks-page/thanks-page.css';

const ThanksPage = () => {
  const dispatch = useAppDispatch();
  const answersDirect = useAppSelector(SliderSlice.selectors.answersDirect);
  const config = useAppSelector(ConfigSlice.selectors.config);

  const { state, setState } = useThanksPageStore();

  useEffect(() => {
    const calculation = calculateDebtAnalysisRaw(answersDirect);

    if (config && config.calculationPageUrlName) {
      window.history.pushState('', '', config.calculationPageUrlName);
    }

    const timer = setTimeout(() => {
      setState({
        type: 'SET_CALCULATION_RESULT',
        payload: {
          monthlyRate: calculation.monthlyRate,
          term: calculation.term,
          isCalculationValid: calculation.isCalculationValid,
          eingabeB6: calculation.eingabeB6,
          totalRepayment: calculation.totalRepayment,
        },
      });

      dispatch(
        GeneralSlice.actions.setDebtAnalysis({
          rate: getFormattedMoney(roundToDecimals(calculation.monthlyRate)),
          months: Math.round(calculation.term).toString(),
        }),
      );

      if (calculation.isCalculationValid) {
        setState({ type: 'SHOW_QUIZ' });
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [answersDirect, config]);

  const { showPage, isCalculationValid, monthRate, runningTime, eingabeB6, totalRepayment } = state;

  if (!showPage) {
    return (
      <div className='loading-container' style={{ textAlign: 'center' }}>
        <div className='circle-loader' />
      </div>
    );
  }

  // "Monatliche Rate" shown to the customer is rounded to the nearest 10 - only the term is
  // rounded (not ceiled), to match Excel.
  const calculatedMonthRate = roundToDecimals(monthRate);
  const runningTimeRounded = Math.round(runningTime);
  // "Statt X zahlen Sie nur Y zurück" must match the sheet exactly (Eingabe!B11, already capped
  // at Eingabe!B6). Recomputing it from the rounded display rate x rounded term used to under-
  // or overshoot the sheet's number - most visibly in "fully repaid" cases, which should show no
  // reduction at all but were producing a lower total instead.
  const paidDebt = totalRepayment;
  const potentialSaving = Math.max(eingabeB6 - totalRepayment, 0);

  return (
    <div className='thanks-container'>
      <h1 className='tp-h1'>Vielen Dank für Ihre Anfrage.</h1>
      <div className='tp-page-container'>
        <div className='tp-head'>
          <h3 className='tp-h3'>Herzlichen Glückwunsch!</h3>
          {isCalculationValid ? (
            <h4 className='tp-h4'>Eine Entschuldung ohne Insolvenz könnte möglich sein</h4>
          ) : (
            <h4 className='tp-h4'>Sie haben den ersten Schritt in eine schuldenfreie Zukunft gemacht</h4>
          )}
        </div>
        {isCalculationValid && (
          <div className='tp-paragraph'>
            Eine erste Analyse Ihrer Angaben ergibt folgende Möglichkeit für eine Schuldenregulierung:
          </div>
        )}
        {isCalculationValid && (
          <div className='tp-berechnung'>
            <ul className='tp-list'>
              <li>
                <strong>Monatliche Rate:</strong> {getFormattedMoney(calculatedMonthRate)} Euro *
              </li>
              <li>
                <strong>Schuldenfrei in:</strong> {runningTimeRounded} Monaten
              </li>
              <li>
                <strong>Ziel:</strong> Vermeidung der Insolvenz
              </li>
              {potentialSaving > 0 && (
                <div style={{ borderTop: '1px solid #e3e3e3', marginTop: '10px', paddingTop: '10px' }}>
                  <li>
                    <strong>Ergebnis:</strong> Statt {getFormattedMoney(eingabeB6)} Euro zahlen Sie nur{' '}
                    {getFormattedMoney(paidDebt)} Euro zurück
                  </li>
                  <li>
                    <strong>Mögliche Ersparnis:</strong>{' '}
                    <span style={{ textDecoration: 'underline' }}>{getFormattedMoney(potentialSaving)} Euro</span> *
                  </li>
                </div>
              )}
            </ul>
          </div>
        )}
        {isCalculationValid ? (
          <div className='tp-paragraph'>
            Sofern eine Ratenzahlung für Sie nicht möglich ist, informieren wir Sie auch gerne kostenlos über die
            Möglichkeit eines Privatinsolvenzverfahrens.
            <br />
            <br />
            Im Rahmen eines <strong>kostenlosen und unverbindlichen</strong> Telefonats wird Ihnen unser geschultes Team
            das Ergebnis des Schuldenchecks erläutern und offene Fragen mit Ihnen klären.
          </div>
        ) : (
          <div className='tp-paragraph'>
            Um Ihren Fall besser bewerten zu können, benötigen wir noch weitere Informationen.
            <br />
            <br />
            Im Rahmen eines <strong>kostenlosen und unverbindlichen</strong> Telefonats wird Ihnen unser geschultes Team
            das Ergebnis des Schuldenchecks erläutern und offene Fragen mit Ihnen klären.
          </div>
        )}
      </div>
      {isCalculationValid && (
        <div className='tp-footer'>
          <div className='tp-footer-star-wrapper'>
            <div className='tp-footer-star'>*</div>
            <div className='tp-footer-content'>
              Dies ist kein verbindliches Angebot. Die genaue Höhe der monatlichen Rate hängt von weiteren Faktoren ab.
              Die Berechnung findet im Rahmen unseres kostenlosen und unverbindlichen Erstgesprächs statt.
            </div>
          </div>
        </div>
      )}
      <Dialog
        on={state.isQuizShown}
        size='2/3'
        position='center'
        heightMax='70'
        onClose={() => setState({ type: 'HIDE_QUIZ' })}
      >
        <Quiz onCompleteQuiz={() => setState({ type: 'HIDE_QUIZ' })} />
      </Dialog>
    </div>
  );
};

export default ThanksPage;
